import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import api from "@/lib/api";
import { asArray } from "@/lib/safeData";
import { localizeArticles, localizeCategory } from "@/i18n/localizeArticle";
import { BookOpen, Clock, ChevronRight, Newspaper } from "lucide-react";

const Blog = () => {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    api.get("/articles").then((r) => {
      const list = asArray(r.data);
      setItems(list);
      const latest = list[0]?.date;
      if (latest && typeof window !== "undefined") {
        const seen = window.localStorage.getItem("chintamat:blog-last-seen");
        if (!seen || latest > seen) {
          window.localStorage.setItem("chintamat:blog-has-new", "1");
          setHasNew(true);
        } else {
          setHasNew(window.localStorage.getItem("chintamat:blog-has-new") === "1");
        }
      }
    }).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items[0]?.date && typeof window !== "undefined") {
      window.localStorage.setItem("chintamat:blog-last-seen", items[0].date);
      window.localStorage.removeItem("chintamat:blog-has-new");
      setHasNew(false);
    }
  }, [items]);

  const articleList = useMemo(() => localizeArticles(items, lang), [items, lang]);

  const categories = useMemo(() => {
    const keys = Array.from(new Set(articleList.map((a) => a.categoryKey).filter(Boolean)));
    return ["all", ...keys];
  }, [articleList]);

  const visible = useMemo(() => {
    if (active === "all") return articleList;
    return articleList.filter((a) => a.categoryKey === active);
  }, [articleList, active]);

  const featured = visible[0];
  const rest = visible.slice(1);

  const b = t.blog || {};
  const langCls = langClass(lang);

  const categoryLabel = (c) => (c === "all" ? b.allStories || "All Stories" : localizeCategory(c, lang));

  useEffect(() => {
    setActive("all");
  }, [lang]);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24" data-testid="blog-page">
      <SectionHeading
        eyebrow={b.eyebrow}
        title={
          <>
            {b.title}
            {hasNew && (
              <span className="ml-2 align-middle text-[10px] uppercase tracking-widest bg-[#D4AF37] text-black px-2 py-0.5 rounded-full font-mono-alt">
                {b.newBadge || "New"}
              </span>
            )}
          </>
        }
        subtitle={b.subtitle}
      />

      <div className="mt-10 -mx-4 px-4 overflow-x-auto pb-2 md:mx-0 md:px-0" data-testid="blog-filters">
        <div className="flex flex-nowrap gap-2 min-w-min">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-mono-alt uppercase tracking-widest border transition-colors whitespace-nowrap ${
                active === c ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/15 text-white/70 hover:border-white/30"
              }`}
              data-testid={`blog-filter-${c}`}
            >
              {categoryLabel(c)}
            </button>
          ))}
        </div>
      </div>

      {featured && (
        <article className="mt-10 surface-card p-6 md:p-10 relative overflow-hidden" data-testid={`blog-featured-${featured.id}`}>
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#D4AF37]/[0.06] blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] font-mono-alt uppercase tracking-widest">
                <Newspaper className="h-3.5 w-3.5" /> {b.featured} · {featured.category}
              </div>
              <h2 className={`mt-3 font-display text-2xl md:text-3xl font-black leading-tight ${langCls}`}>{featured.title}</h2>
              <p className={`mt-3 text-white/70 leading-relaxed ${langCls}`}>{featured.excerpt}</p>
              <div className="mt-5 flex items-center gap-4 text-xs text-white/45 font-mono-alt">
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.read_minutes} {t.education.readMinutes}</span>
                <span>·</span>
                <span>{featured.date}</span>
              </div>
            </div>
            <div className="md:col-span-2 surface-card p-5 bg-black/30">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-alt">{b.readFull}</div>
              <p className={`mt-2 text-sm text-white/80 leading-relaxed line-clamp-6 ${langCls}`}>{featured.body}</p>
            </div>
          </div>
        </article>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
        {rest.map((a) => (
          <article key={a.id} className="surface-card p-6 flex flex-col group" data-testid={`blog-card-${a.id}`}>
            <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] font-mono-alt uppercase tracking-widest">
              <BookOpen className="h-3.5 w-3.5" /> {a.category}
            </div>
            <h3 className={`mt-3 font-display text-lg font-bold leading-snug ${langCls}`}>{a.title}</h3>
            <p className={`mt-2 text-sm text-white/65 leading-relaxed flex-1 ${langCls}`}>{a.excerpt}</p>
            <details className="mt-3 group/details">
              <summary className="text-xs text-[#D4AF37] hover:text-yellow-400 cursor-pointer inline-flex items-center gap-1 font-mono-alt uppercase tracking-widest list-none">
                <span className="group-open/details:hidden">{b.readArticle}</span>
                <span className="hidden group-open/details:inline">{b.collapse}</span>
                <ChevronRight className="h-3 w-3 transition-transform group-open/details:rotate-90" />
              </summary>
              <p className={`mt-3 text-[13px] text-white/75 leading-relaxed whitespace-pre-line ${langCls}`}>{a.body}</p>
            </details>
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/45 font-mono-alt">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {a.read_minutes} {t.education.readMinutes}</span>
              <span>{a.date}</span>
            </div>
          </article>
        ))}
      </div>

      {visible.length === 0 && (
        <div className={`surface-card p-10 text-center text-white/55 mt-10 ${langCls}`}>{b.emptyCategory}</div>
      )}

      <div className="mt-16 surface-card p-8 text-center">
        <h3 className={`font-display text-xl font-bold ${langCls}`}>{b.ctaTitle}</h3>
        <p className={`mt-2 text-sm text-white/60 max-w-xl mx-auto ${langCls}`}>{b.ctaBody}</p>
        <Link to="/#loss-categories" className="inline-flex mt-5 px-5 py-2.5 rounded-md bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold text-sm">
          {b.ctaButton}
        </Link>
      </div>
    </div>
  );
};

export default Blog;
